import {
  Box,
  Card,
  Grid,
  Modal,
  TextField,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  CircularProgress,
  IconButton,
  Avatar,
  Badge,
  Divider,
  Paper,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import React, { useEffect, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { createOfferAction, resetCreateOfferState } from "../../redux/toolkit/offer/createOffer";
import { getAllOffersAction } from "../../redux/toolkit/offer/getAllOffers";
import { getAllPartnerActionNoLimit } from "../../redux/toolkit/partner/getAllPartnerNoLimit";
import { uplodImageAction } from "../../redux/toolkit/image/uplodImage";
import SnackBarUI from "../../comman/SnackBarUI";

const offerSchema = yup.object({
  name: yup.string().required("Offer Name is required"),
  track_link: yup.string().required("Track Link is required"),
  partner_id: yup.string().required("Partner is required"),
  p_offer_id: yup.string().required("Offer ID is required"),
  category: yup.string(),
  description: yup.string(),
  short_description: yup.string(),
  video_link: yup.string(),
  important_tips: yup.string(),
  conditions: yup.string(),
}).required();

const rewardInitial = {
  level: "",
  payout_amount: 0,
  revenue_amount: 0,
  minutes_played: 0,
  description: "",
  day: 0,
  r_id: "",
  reward_type: 1,
  max_retry: 0,
};

export default function AddOffer({ state, setState, pageState, search, partnerId }) {
  const [snack, setSnack] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const [byAdmin, setByAdmin] = useState(true);
  const [baseImage, setBaseImage] = useState("");
  const [coverImage, setCoverImage] = useState("");
  const [baseImageLoading, setBaseImageLoading] = useState(false);
  const [coverImageLoading, setCoverImageLoading] = useState(false);
  const [rewards, setRewards] = useState([]);
  const [currentReward, setCurrentReward] = useState(rewardInitial);
  const [editingRewardIndex, setEditingRewardIndex] = useState(null);

  const { register, setValue, handleSubmit, formState: { errors }, reset, control } = useForm({
    resolver: yupResolver(offerSchema),
  });

  const partnerIdValue = useWatch({ control, name: "partner_id" });

  const dispatch = useDispatch();
  const createOfferSelector = useSelector((state) => state.createOffer);
  const { data: partnersData } = useSelector((state) => state.allPartnerWithoutLimit);
  const { status, message, loading } = createOfferSelector;

  useEffect(() => {
    dispatch(getAllPartnerActionNoLimit());
    // Reset create offer state when component mounts
    dispatch(resetCreateOfferState());
  }, [dispatch]);

  useEffect(() => {
    if (status === 200) {
      setSnack(true);
      reset();
      setState(false);
      dispatch(
        getAllOffersAction({
          page: pageState.page,
          pageSize: pageState.pageSize,
          search: search,
          partner_id: partnerId,
        })
      );
    }
  }, [status]);

  const handleAddOffer = async (data) => {
    data.is_active = isActive;
    data.by_admin = byAdmin;
    data.image = {
      base: baseImage || "",
      cover_image: coverImage || "",
    };
    data.rewards = rewards;
    await dispatch(createOfferAction({ ...data }));
  };

  const handleClose = () => {
    setState(false);
    reset();
    setRewards([]);
    setCurrentReward(rewardInitial);
    setBaseImage("");
    setCoverImage("");
    // Reset create offer state when modal closes
    dispatch(resetCreateOfferState());
  };

  const handleBaseImageUpload = async (e) => {
    setBaseImageLoading(true);
    const formData = new FormData();
    const file = e.target.files[0];
    formData.append("file", file);

    try {
      const uploadedImg = await dispatch(uplodImageAction(formData));
      const imgStr = uploadedImg?.payload?.data?.url;
      setBaseImage(imgStr);
      alert(`Base Image is Successfully Uploaded`);
    } catch (error) {
      alert("Failed to upload base image");
    }
    setBaseImageLoading(false);
  };

  const handleCoverImageUpload = async (e) => {
    setCoverImageLoading(true);
    const formData = new FormData();
    const file = e.target.files[0];
    formData.append("file", file);

    try {
      const uploadedImg = await dispatch(uplodImageAction(formData));
      const imgStr = uploadedImg?.payload?.data?.url;
      setCoverImage(imgStr);
      alert(`Cover Image is Successfully Uploaded`);
    } catch (error) {
      alert("Failed to upload cover image");
    }
    setCoverImageLoading(false);
  };

  const handleRewardChange = (e) => {
    const { name, value } = e.target;
    setCurrentReward((prev) => ({
      ...prev,
      [name]: name === "level" || name === "description" || name === "r_id" ? value : Number(value),
    }));
  };

  const handleAddReward = () => {
    if (!currentReward.level) {
      alert("Level is required for reward");
      return;
    }
    
    if (editingRewardIndex !== null) {
      // Update existing reward
      setRewards((prev) =>
        prev.map((reward, index) =>
          index === editingRewardIndex ? currentReward : reward
        )
      );
      setEditingRewardIndex(null);
    } else {
      // Add new reward
      setRewards((prev) => [...prev, currentReward]);
    }
    setCurrentReward(rewardInitial);
  };

  const handleEditReward = (index) => {
    setCurrentReward(rewards[index]);
    setEditingRewardIndex(index);
  };

  const handleCancelEdit = () => {
    setCurrentReward(rewardInitial);
    setEditingRewardIndex(null);
  };

  const handleDeleteReward = (index) => {
    setRewards((prev) => prev.filter((_, i) => i !== index));
    // If we're editing this reward, cancel the edit
    if (editingRewardIndex === index) {
      setCurrentReward(rewardInitial);
      setEditingRewardIndex(null);
    }
  };

  return (
    <>
      <Modal sx={{ overflow: "scroll" }} open={state} onClose={handleClose}>
        <Box className="modal_box">
          <Card sx={{ width: 800, p: 5, maxHeight: "90vh", overflow: "auto" }}>
            <Typography variant="h5" sx={{ mb: 3 }} align="center">
              Create Offer
            </Typography>

            <form onSubmit={handleSubmit(handleAddOffer)}>
              <Grid container spacing={2}>
                {/* Basic Info */}
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    Basic Information
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                </Grid>

                {/* Offer Name */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Offer Name"
                    variant="outlined"
                    placeholder="Offer Name"
                    {...register("name")}
                    error={!!errors?.name?.message}
                    helperText={errors?.name?.message}
                  />
                </Grid>

                {/* Offer ID */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Offer ID (p_offer_id)"
                    variant="outlined"
                    placeholder="Offer ID"
                    {...register("p_offer_id")}
                    error={!!errors?.p_offer_id?.message}
                    helperText={errors?.p_offer_id?.message}
                  />
                </Grid>

                {/* Partner */}
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth error={!!errors?.partner_id?.message}>
                    <InputLabel>Partner</InputLabel>
                    <Select
                      label="Partner"
                      value={partnerIdValue || ""}
                      {...register("partner_id")}
                      onChange={(e) => setValue("partner_id", e.target.value)}
                    >
                      {partnersData?.map((partner) => (
                        <MenuItem key={partner._id} value={partner._id}>
                          {partner.name}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors?.partner_id?.message && (
                      <Typography variant="caption" color="error">
                        {errors?.partner_id?.message}
                      </Typography>
                    )}
                  </FormControl>
                </Grid>

                {/* Category */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Category"
                    variant="outlined"
                    placeholder="Category"
                    defaultValue="All"
                    {...register("category")}
                    error={!!errors?.category?.message}
                    helperText={errors?.category?.message}
                  />
                </Grid>

                {/* Images Section */}
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ mb: 1, mt: 2 }}>
                    Images
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                </Grid>

                {/* Base Image */}
                <Grid item xs={12} sm={6}>
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Base Image
                    </Typography>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleBaseImageUpload}
                      style={{ display: "block", marginBottom: 10 }}
                    />
                    {baseImageLoading ? (
                      <CircularProgress size={24} />
                    ) : (
                      baseImage && (
                        <Avatar
                          src={baseImage}
                          sx={{ width: 100, height: 100, borderRadius: "8px" }}
                        />
                      )
                    )}
                  </Box>
                </Grid>

                {/* Cover Image */}
                <Grid item xs={12} sm={6}>
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Cover Image
                    </Typography>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleCoverImageUpload}
                      style={{ display: "block", marginBottom: 10 }}
                    />
                    {coverImageLoading ? (
                      <CircularProgress size={24} />
                    ) : (
                      coverImage && (
                        <Avatar
                          src={coverImage}
                          sx={{ width: 100, height: 100, borderRadius: "8px" }}
                        />
                      )
                    )}
                  </Box>
                </Grid>

                {/* Rewards Section */}
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ mb: 1, mt: 2 }}>
                    Rewards
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                </Grid>

                {/* Add Reward Form */}
                <Grid item xs={12}>
                  <Paper sx={{ p: 2, bgcolor: "#f5f5f5" }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Level"
                          name="level"
                          value={currentReward.level}
                          onChange={handleRewardChange}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          label="Payout Amount"
                          name="payout_amount"
                          value={currentReward.payout_amount}
                          onChange={handleRewardChange}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          label="Revenue Amount"
                          name="revenue_amount"
                          value={currentReward.revenue_amount}
                          onChange={handleRewardChange}
                        />
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          label="Minutes Played"
                          name="minutes_played"
                          value={currentReward.minutes_played}
                          onChange={handleRewardChange}
                        />
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          label="Day"
                          name="day"
                          value={currentReward.day}
                          onChange={handleRewardChange}
                        />
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Reward ID"
                          name="r_id"
                          value={currentReward.r_id}
                          onChange={handleRewardChange}
                        />
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Reward Type</InputLabel>
                          <Select
                            label="Reward Type"
                            name="reward_type"
                            value={currentReward.reward_type}
                            onChange={handleRewardChange}
                          >
                            <MenuItem value={1}>Postback</MenuItem>
                            <MenuItem value={2}>App Usage</MenuItem>
                            <MenuItem value={3}>Proof</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          label="Max Retry"
                          name="max_retry"
                          value={currentReward.max_retry}
                          onChange={handleRewardChange}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Description"
                          name="description"
                          value={currentReward.description}
                          onChange={handleRewardChange}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <Button
                            variant="contained"
                            startIcon={editingRewardIndex !== null ? <EditIcon /> : <AddIcon />}
                            onClick={handleAddReward}
                            size="small"
                          >
                            {editingRewardIndex !== null ? "Update Reward" : "Add Reward"}
                          </Button>
                          {editingRewardIndex !== null && (
                            <Button
                              variant="outlined"
                              onClick={handleCancelEdit}
                              size="small"
                            >
                              Cancel Edit
                            </Button>
                          )}
                        </Box>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>

                {/* Display Added Rewards */}
                {rewards.length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Added Rewards ({rewards.length})
                    </Typography>
                      {rewards.map((reward, index) => (
                        <Paper 
                          key={index} 
                          sx={{ 
                            p: 2, 
                            mb: 1, 
                            bgcolor: editingRewardIndex === index ? "#fff3e0" : "#e3f2fd",
                            border: editingRewardIndex === index ? "2px solid #ff9800" : "none"
                          }}
                        >
                          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="body2">
                                <strong>Level:</strong> {reward.level} | 
                                <strong> Payout:</strong> {reward.payout_amount} | 
                                <strong> Revenue:</strong> {reward.revenue_amount}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Type: {reward.reward_type === 1 ? "Postback" : reward.reward_type === 2 ? "App Usage" : "Proof"} | 
                                Day: {reward.day} | Minutes: {reward.minutes_played}
                              </Typography>
                            </Box>
                            <Box sx={{ display: "flex", gap: 1 }}>
                              <IconButton
                                color="primary"
                                size="small"
                                onClick={() => handleEditReward(index)}
                                disabled={editingRewardIndex !== null && editingRewardIndex !== index}
                              >
                                <EditIcon />
                              </IconButton>
                              <IconButton
                                color="error"
                                size="small"
                                onClick={() => handleDeleteReward(index)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Box>
                          </Box>
                        </Paper>
                      ))}
                  </Grid>
                )}

                {/* Other Details */}
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ mb: 1, mt: 2 }}>
                    Other Details
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                </Grid>

                {/* Track Link */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Track Link"
                    variant="outlined"
                    placeholder="Track Link"
                    {...register("track_link")}
                    error={!!errors?.track_link?.message}
                    helperText={errors?.track_link?.message}
                  />
                </Grid>

                {/* Total Payout Amount - Auto-calculated from rewards */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Total Payout Amount (Auto-calculated)"
                    variant="outlined"
                    placeholder="Total Payout Amount"
                    inputProps={{ step: "0.01", min: 0 }}
                    disabled
                    value={rewards.reduce((sum, reward) => sum + (Number(reward.payout_amount) || 0), 0).toFixed(2)}
                    helperText="Auto-calculated from rewards payout amounts"
                  />
                </Grid>

                {/* Video Link */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Video Link"
                    variant="outlined"
                    placeholder="Video Link"
                    {...register("video_link")}
                    error={!!errors?.video_link?.message}
                    helperText={errors?.video_link?.message}
                  />
                </Grid>

                {/* Short Description */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    label="Short Description"
                    variant="outlined"
                    placeholder="Short Description"
                    {...register("short_description")}
                    error={!!errors?.short_description?.message}
                    helperText={errors?.short_description?.message}
                  />
                </Grid>

                {/* Description */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Description"
                    variant="outlined"
                    placeholder="Description"
                    {...register("description")}
                    error={!!errors?.description?.message}
                    helperText={errors?.description?.message}
                  />
                </Grid>

                {/* Important Tips */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    label="Important Tips"
                    variant="outlined"
                    placeholder="Important Tips"
                    {...register("important_tips")}
                    error={!!errors?.important_tips?.message}
                    helperText={errors?.important_tips?.message}
                  />
                </Grid>

                {/* Conditions */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    label="Conditions"
                    variant="outlined"
                    placeholder="Conditions"
                    {...register("conditions")}
                    error={!!errors?.conditions?.message}
                    helperText={errors?.conditions?.message}
                  />
                </Grid>

                {/* Switches */}
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={isActive}
                        onChange={(e) => setIsActive(e.target.checked)}
                      />
                    }
                    label="Is Active"
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
                    <Typography variant="body1">
                      <strong>Created By Admin:</strong> {byAdmin ? "Yes" : "No"}
                    </Typography>
                  </Box>
                </Grid>

                {/* Action Buttons */}
                <Grid item xs={12}>
                  <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end", mt: 2 }}>
                    <Button variant="outlined" onClick={handleClose}>
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={loading}
                      startIcon={loading && <CircularProgress size={20} />}
                    >
                      {loading ? "Creating..." : "Create Offer"}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </form>
          </Card>
        </Box>
      </Modal>

      {snack && (
        <SnackBarUI
          open={snack}
          setOpen={setSnack}
          message={message || ""}
          type={status === 200 ? "success" : "error"}
        />
      )}
    </>
  );
}
